import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import "@uiw/react-markdown-preview/markdown.css";
import "highlight.js/styles/github.css";

import { IntegrationCopyType } from "@/components/flow/shared/Deploy/documentation/types";
import { Dropdown, DropdownItem } from "@scripts/react/components/Dropdown/Dropdown";
import { twClassNames } from "@scripts/react/helpers/twClassNames";
import { GetAPIKeyButton } from "../GetAPIKeyButton";
import { useAnalytics } from "@/contexts/AnalyticsContext";
import { AnalyticsFlowEvent } from "@/analytics/types";
import { useFlow } from "@/contexts/FlowContext";
import { useAuth } from "@/contexts/AuthContext";
import { useDeployContext } from "@/contexts/DeployContext";
import { Chip } from "@scripts/react/components/Chip";
import Button from "@scripts/react/components/Button";
import { MarkdownPreview } from "@scripts/react/components/MarkdownPreview";
import { TooltipContainer } from "@scripts/react/components/Tooltip.jsx";
import { projectSelector } from "@rflocal/shared";
import { DropdownMenu } from "@/components/DropdownMenu";

// Patterns to match API keys in different code formats
const patterns = [
    // cURL format: "api_key": "actual_api_key"
    /("api_key":\s*")([^"]+)(")/g,
    // JavaScript format: api_key: 'actual_api_key'
    /(api_key:\s*['"])([^'"]+)(['"])/g,
    // Python format: api_key="actual_api_key"
    /(api_key\s*=\s*["'])([^"']+)(["'])/g,
    // Generic API key patterns
    /(apiKey\s*=\s*["'])([^"']+)(["'])/g,
    /("apiKey":\s*")([^"]+)(")/g
];

const censoredPatterns = [
    /("api_key":\s*")(\*{8})(")/g,
    /(api_key:\s*['"])(\*{8})(['"])/g,
    /(api_key\s*=\s*["'])(\*{8})(["'])/g,
    /(apiKey\s*=\s*["'])(\*{8})(["'])/g,
    /("apiKey":\s*")(\*{8})(")/g
];

// Function to censor API keys in code snippets
const censorApiKeys = (code: string): string => {
    let processedCode = code;

    patterns.forEach((pattern) => {
        processedCode = processedCode.replace(pattern, (match, prefix, apiKey, suffix) => {
            // Replace the API key with exactly 8 asterisks
            const censoredKey = "*".repeat(8);
            return prefix + censoredKey + suffix;
        });
    });

    return processedCode;
};

// Language options matching RunWorkflowModal
const LANGUAGES: DropdownItem[] = [
    { text: "Python", value: "python" },
    { text: "JavaScript Fetch", value: "javascript" },
    { text: "HTTP / cURL", value: "curl" }
];

// Function to get chips based on language
const getChipsForLanguage = (language: string) => {
    switch (language) {
        case "curl":
            return ["Testing", "Scripting", "Automation"];
        case "python":
            return ["Data Analysis", "ML Workflows"];
        case "javascript":
            return ["Web Apps", "Interactive UIs"];
        default:
            return [];
    }
};

// Numbered step component (matching RunWorkflowModal pattern)
function NumberedStep({
    number,
    title,
    children,
    className
}: {
    number: number;
    title: string;
    children?: React.ReactNode;
    className?: string;
}) {
    return (
        <div className={twClassNames("flex min-w-0 justify-start gap-3", className)}>
            <Chip
                color="brand"
                size="large"
                className="h-8 flex-shrink-0 rounded-full text-center text-sm font-bold"
            >
                {number}
            </Chip>
            <div className="flex min-h-0 min-w-0 flex-1 flex-col gap-3">
                <h3 className="text-base font-semibold text-primary">{title}</h3>
                {children}
            </div>
        </div>
    );
}

const CustomDropdownItem = (item: DropdownItem): React.ReactElement => {
    let itemValue = item.value;

    if (!itemValue) {
        const matchedLang = LANGUAGES.find((lang) => lang.text === item.text);
        itemValue = matchedLang?.value;
    }

    const chips = itemValue ? getChipsForLanguage(String(itemValue)) : [];

    return (
        <span className="flex w-full items-center justify-between gap-2 text-sm font-medium">
            <span className="flex items-center gap-2 truncate">
                {item.text || String(item.value)}
            </span>
            {chips.length > 0 && (
                <span className="flex flex-shrink-0 gap-1">
                    {chips.map((label: string, index: number) => (
                        <Chip key={index} color="gray">
                            {label}
                        </Chip>
                    ))}
                </span>
            )}
        </span>
    );
};

// Deployment credential component
function DeploymentCredential({
    label,
    value,
    fieldId,
    copiedField,
    onCopy,
    masked = false
}: {
    label: string;
    value: string;
    fieldId: string;
    copiedField: string | null;
    onCopy: (fieldId: string, value: string) => void;
    masked?: boolean;
}) {
    const isCopied = copiedField === fieldId;
    const displayValue = masked ? "*".repeat(value.length) : value;

    return (
        <div>
            <span className="text-sm font-semibold text-primary">{label}</span>
            <div
                className="group flex w-full cursor-pointer items-center justify-between gap-4 rounded-lg border border-white py-1 text-sm font-normal text-secondary hover:border-gray-300 hover:bg-gray-50"
                onClick={() => onCopy(fieldId, value)}
            >
                {displayValue}{" "}
                <i
                    className={`far ${isCopied ? "fa-check text-success" : "fa-copy"} text-tertiary opacity-0 transition-opacity group-hover:opacity-100`}
                ></i>
            </div>
        </div>
    );
}

function LanguageSelector({
    selectedLanguage,
    onLanguageSelect
}: {
    selectedLanguage: DropdownItem;
    onLanguageSelect: (language: DropdownItem) => void;
}) {
    const selectedChips = getChipsForLanguage(String(selectedLanguage.value));

    return (
        <Dropdown
            theme="light"
            title=""
            titlePrefix={
                <>
                    <span>{selectedLanguage.text}</span>
                    <span className="flex flex-shrink-0 gap-1">
                        {selectedChips.map((label: string, index: number) => (
                            // @ts-expect-error - Type mismatch between React type definitions
                            <Chip key={index} color="gray" size="sm">
                                {label}
                            </Chip>
                        ))}
                    </span>
                </>
            }
            titlePrefixClassName="flex flex-1 items-center justify-between gap-2"
            onItemSelected={onLanguageSelect}
            defaultSelectedItem={selectedLanguage}
            items={LANGUAGES}
            itemComponent={CustomDropdownItem as any}
            className="[&_div.btn2]:!h-10 [&_div.btn2]:!border-gray-300"
        />
    );
}

// AI Service button configuration
const AI_SERVICES = [
    { name: "ChatGPT", url: "https://chatgpt.com/?q=", logo: "/images/llm-logos/chatgpt.svg" },
    { name: "Claude", url: "https://claude.ai/new?q=", logo: "/images/llm-logos/claude.svg" },
    {
        name: "Lovable",
        url: "https://lovable.dev/?autosubmit=true#prompt=",
        logo: "/images/llm-logos/lovable-logo-icon.svg"
    }
] as const;

function AIServiceButton({
    service,
    onClick
}: {
    service: (typeof AI_SERVICES)[number];
    onClick: () => void;
}) {
    return (
        <Button
            onClick={onClick}
            type="secondary"
            className="flex items-center gap-2 !border-gray-300"
            size="small"
        >
            <img src={service.logo} alt="" className="h-4 w-4" />
            {service.name}
        </Button>
    );
}

function TooltipChip({
    icon,
    title,
    label,
    onClick,
    showCheck = false
}: {
    icon: string;
    title: string;
    label: string;
    onClick: () => void;
    showCheck?: boolean;
}) {
    return (
        // @ts-expect-error - Type mismatch between React type definitions
        <TooltipContainer
            type="titleOnly"
            title={title}
            customButton={
                // @ts-expect-error - Type mismatch between React type definitions
                <Chip
                    type="text"
                    color="white"
                    onClick={onClick}
                    className="cursor-pointer self-center hover:bg-gray-100"
                >
                    {showCheck ? (
                        <>
                            <i className="far fa-check text-success"></i>
                            Copied!
                        </>
                    ) : (
                        <>
                            <i className={`far ${icon}`}></i>
                            {label}
                        </>
                    )}
                </Chip>
            }
        />
    );
}

interface LLMActionItem {
    label: string;
    value: string;
    description: string;
    logo?: string;
}

const LLM_ACTION_ITEMS: LLMActionItem[] = [
    { label: "Copy page", value: "copy", description: "Copy page as markdown" },
    {
        label: "Open in ChatGPT",
        value: "chatgpt",
        description: "Ask ChatGPT about this",
        logo: "/images/llm-logos/chatgpt.svg"
    },
    {
        label: "Open in Claude",
        value: "claude",
        description: "Ask Claude about this",
        logo: "/images/llm-logos/claude.svg"
    },
    {
        label: "Open in Lovable",
        value: "lovable",
        description: "Build a vision app in Lovable",
        logo: "/images/llm-logos/lovable-logo-icon.svg"
    }
];

function LLMActionDropdown({
    onCopy,
    onOpenAI,
    justCopied
}: {
    onCopy: () => void;
    onOpenAI: (provider: string) => void;
    justCopied: boolean;
}) {
    const handleItemSelected = (item: LLMActionItem) => {
        if (item.value === "copy") {
            onCopy();
        } else {
            onOpenAI(item.value);
        }
    };

    return (
        <div className="flex items-center">
            <Chip
                onClick={onCopy}
                color="white"
                className="cursor-pointer rounded-r-none hover:bg-gray-100"
            >
                {justCopied ? (
                    <>
                        <i className="far fa-fw fa-check text-success"></i>
                        <span>Copied!</span>
                    </>
                ) : (
                    <>
                        <i className="far fa-fw fa-copy"></i>
                        <span>Copy for LLM</span>
                    </>
                )}
            </Chip>

            <DropdownMenu
                items={LLM_ACTION_ITEMS}
                selectedItem={LLM_ACTION_ITEMS[0]}
                onSelectItem={handleItemSelected}
                getItemLabel={() => ""}
                getItemKey={(item) => item.value}
                getLeftContent={(item) => (
                    <div className="flex w-full flex-col gap-1">
                        <div className="flex gap-2">
                            <div className="flex h-4 w-4">
                                {item.logo ? (
                                    <img src={item.logo} alt="" className="h-4 w-4" />
                                ) : (
                                    <i className="far fa-copy text-sm"></i>
                                )}
                            </div>
                            <span className="font-semibold text-primary">{item.label}</span>
                        </div>
                        <span className="ml-6 text-left text-xs text-secondary">
                            {item.description}
                        </span>
                    </div>
                )}
                customMenuButton={<i className="far fa-chevron-down text-xs"></i>}
                buttonClassName="chip text white cursor-pointer rounded-l-none hover:bg-gray-100 border-l-0 focus:outline-none"
                menuItemsClassName="right-0 top-full mt-1 min-w-[240px]"
                menuItemClassName="!flex-col !items-start !gap-1 border-b border-gray-200 last:border-b-0 !rounded-none"
            />
        </div>
    );
}

export const IntegrationCodeSnippet = ({
    copy,
    blurred,
    isExpanded = true
}: {
    copy: IntegrationCopyType;
    blurred?: boolean;
    isExpanded: boolean;
}) => {
    // Always default to JavaScript
    const [selectedLanguage, setSelectedLanguage] = useState<DropdownItem>(LANGUAGES[1]);
    const [justCopied, setJustCopied] = useState(false);
    const [copiedField, setCopiedField] = useState<string | null>(null);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const { track } = useAnalytics();
    const { task } = useFlow();
    const { user } = useAuth();
    const { workflowCredentials } = useDeployContext();

    // Handler for copying fields
    const handleCopyField = useCallback((fieldId: string, value: string) => {
        navigator.clipboard.writeText(value);
        setCopiedField(fieldId);
        setTimeout(() => setCopiedField(null), 2000);
    }, []);

    // Get the current snippet based on selected language
    const selectedSnippet = useMemo(
        () =>
            copy.codeSnippets.find((snippet) => {
                const envLower = snippet.env.toLowerCase();
                const langValue = String(selectedLanguage.value);

                if (langValue === "python") return envLower.includes("python");
                if (langValue === "javascript") return envLower.includes("javascript");
                if (langValue === "curl")
                    return envLower.includes("curl") || envLower.includes("http");

                return false;
            }) || copy.codeSnippets[0],
        [copy.codeSnippets, selectedLanguage.value]
    );

    // Shared function to fetch and process markdown with credentials
    const fetchProcessedMarkdown = useCallback(async (): Promise<string> => {
        const response = await fetch(`${import.meta.env.BASE_URL}roboflow-integration.md`);
        let markdownContent = await response.text();

        // Replace placeholders with actual values
        return markdownContent
            .replace(/{apikey}/g, workflowCredentials.apiKey)
            .replace(/{workspace}/g, workflowCredentials.workspaceUrl)
            .replace(/{workflow-id}/g, workflowCredentials.workflowUrl);
    }, [
        workflowCredentials.apiKey,
        workflowCredentials.workspaceUrl,
        workflowCredentials.workflowUrl
    ]);

    const openAIService = async (url: string, provider: string) => {
        if (!selectedSnippet) return;

        try {
            const markdownContent = await fetchProcessedMarkdown();
            await navigator.clipboard.writeText(markdownContent);

            // Open AI service with a short message telling user to paste
            const shortMessage = `I've copied a Roboflow computer vision model integration guide to my clipboard. I will paste it here so that you can follow it to integrate my Roboflow model into my project.`;
            const encodedMessage = encodeURIComponent(shortMessage);
            window.open(`${url}${encodedMessage}`, "_blank");

            // Track specific AI service button click
            const eventMap: Record<string, AnalyticsFlowEvent> = {
                chatgpt: AnalyticsFlowEvent.CHATGPT_BUTTON_CLICKED,
                claude: AnalyticsFlowEvent.CLAUDE_BUTTON_CLICKED,
                lovable: AnalyticsFlowEvent.LOVABLE_BUTTON_CLICKED
            };
            const event = eventMap[provider] || AnalyticsFlowEvent.CODE_SNIPPET_COPIED;

            track({ event, data: { task, env: selectedSnippet.env, aiProvider: provider } });
        } catch (error) {
            console.error("Failed to fetch integration guide:", error);
        }
    };

    const copyToClipboard = async () => {
        if (!selectedSnippet) return;

        try {
            const markdownContent = await fetchProcessedMarkdown();
            await navigator.clipboard.writeText(markdownContent);
            setJustCopied(true);
            track({
                event: AnalyticsFlowEvent.MARKDOWN_COPY_CLICKED,
                data: { task, env: selectedSnippet.env }
            });

            // Show feedback for 2 seconds, then reset and close dropdown
            setTimeout(() => {
                setJustCopied(false);
                if (dropdownRef.current) {
                    const button = dropdownRef.current.querySelector(
                        '[role="button"]'
                    ) as HTMLElement;
                    if (button) {
                        button.blur();
                    }
                }
            }, 2000);
        } catch (err) {
            console.error("Failed to copy to clipboard:", err);
        }
    };

    // Handle copy events to replace censored API keys with original ones
    useEffect(() => {
        const handleCopy = (event: ClipboardEvent) => {
            const selection = window.getSelection();
            if (!selection || selection.toString().length === 0) return;

            const selectedText = selection.toString();

            // Check if the selected text contains censored API keys
            const hasCensoredKeys = selectedText.includes("*".repeat(8));

            if (hasCensoredKeys) {
                // Replace the censored keys with the original API keys from the source code
                let originalText = selectedText;

                // For each censored pattern, find and replace with the corresponding original
                censoredPatterns.forEach((censoredPattern, index) => {
                    const originalPattern = patterns[index];
                    const originalMatch = selectedSnippet.code.match(originalPattern);

                    if (originalMatch) {
                        originalText = originalText.replace(censoredPattern, originalMatch[0]);
                    }
                });

                event.clipboardData?.setData("text/plain", originalText);
                event.preventDefault();
            }

            track({
                event: AnalyticsFlowEvent.CODE_SNIPPET_COPIED,
                data: { task, env: selectedSnippet.env }
            });
        };

        document.addEventListener("copy", handleCopy);

        return () => {
            document.removeEventListener("copy", handleCopy);
        };
    }, [selectedSnippet.code, selectedSnippet.env, task, track]);

    return (
        <div
            className={twClassNames(
                "flex flex-col rounded-xl border border-gray-300 bg-white font-medium shadow-lg",
                isExpanded && "h-full"
            )}
        >
            <div className="flex w-full flex-shrink-0 justify-between border-b px-6 py-3">
                <div className="flex items-center gap-2 text-base font-medium">
                    Integrate My Model
                    <Chip type="text" color="gray">
                        <i className="far fa-clock"></i>5 Minutes
                    </Chip>
                    {user && (
                        <TooltipChip
                            icon="fa-coins"
                            title="Open Credit Usage"
                            label="~1 Credit/500 Images"
                            onClick={() => {
                                window.open(
                                    `${projectSelector.appUrl(false, true)}/${workflowCredentials.workspaceUrl}/settings/plan`,
                                    "_blank"
                                );
                            }}
                        />
                    )}
                </div>
                <div className="flex gap-2">
                    {user && (
                        <LLMActionDropdown
                            onCopy={copyToClipboard}
                            onOpenAI={(provider) => {
                                const serviceMap: Record<string, (typeof AI_SERVICES)[number]> = {
                                    chatgpt: AI_SERVICES[0],
                                    claude: AI_SERVICES[1],
                                    lovable: AI_SERVICES[2]
                                };
                                const service = serviceMap[provider];
                                if (service) {
                                    openAIService(service.url, provider);
                                }
                            }}
                            justCopied={justCopied}
                        />
                    )}
                    <TooltipChip
                        icon="fa-book"
                        title="Open Docs"
                        label="Documentation"
                        onClick={() => {
                            track({
                                event: AnalyticsFlowEvent.DOCS_BUTTON_CLICKED,
                                data: { task }
                            });
                            window.open("https://docs.roboflow.com/deploy/serverless", "_blank");
                        }}
                    />
                </div>
            </div>
            <div className="flex min-h-0 flex-1">
                {user && (
                    <div className="flex w-1/4 flex-col justify-between border-r px-6 py-6">
                        <div>
                            <div className="flex flex-col gap-3">
                                <DeploymentCredential
                                    label="API Key"
                                    value={workflowCredentials.apiKey}
                                    fieldId="apiKey"
                                    copiedField={copiedField}
                                    onCopy={handleCopyField}
                                    masked={true}
                                />
                                <DeploymentCredential
                                    label="Workspace Name"
                                    value={workflowCredentials.workspaceUrl}
                                    fieldId="workspaceUrl"
                                    copiedField={copiedField}
                                    onCopy={handleCopyField}
                                />
                                <DeploymentCredential
                                    label="Workflow ID Name"
                                    value={workflowCredentials.workflowUrl}
                                    fieldId="workflowUrl"
                                    copiedField={copiedField}
                                    onCopy={handleCopyField}
                                />
                                <Button
                                    type="secondary"
                                    size="small"
                                    className="w-full"
                                    onClick={() => {
                                        track({
                                            event: AnalyticsFlowEvent.OPEN_WORKFLOWS_BUTTON_CLICKED,
                                            data: {
                                                workflowUrl: workflowCredentials.workflowUrl,
                                                workspaceUrl: workflowCredentials.workspaceUrl
                                            }
                                        });
                                        window.open(
                                            `${projectSelector.appUrl(false, true)}/${workflowCredentials.workspaceUrl}/workflows/edit/${workflowCredentials.workflowUrl}`,
                                            "_blank"
                                        );
                                    }}
                                >
                                    Open Workflows
                                </Button>
                            </div>
                        </div>
                    </div>
                )}
                <div className={twClassNames("flex min-h-0 min-w-0", user ? "w-3/4" : "w-full")}>
                    <div className="flex min-w-0 flex-1 flex-col gap-8 overflow-scroll p-6">
                        <span className="text-sm font-normal text-secondary">
                            Your model is ready to use as a cloud API! Find the code you need to run
                            your model in production below. <br></br>
                            {user && (
                                <>
                                    You can access your model again anytime through your{" "}
                                    <a
                                        href={`${projectSelector.appUrl(false, true)}/${workflowCredentials.workspaceUrl}/workflows/edit/${workflowCredentials.workflowUrl}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-brand-500 hover:underline"
                                    >
                                        Roboflow Workspace
                                    </a>
                                    .
                                </>
                            )}
                        </span>
                        <NumberedStep number={1} title="Choose Language">
                            <LanguageSelector
                                selectedLanguage={selectedLanguage}
                                onLanguageSelect={setSelectedLanguage}
                            />
                        </NumberedStep>

                        {selectedLanguage.value !== "curl" && (
                            <NumberedStep number={2} title="Install Library">
                                {/* @ts-expect-error - Type mismatch between React type definitions */}
                                <MarkdownPreview
                                    source={`\`\`\`bash\n${
                                        selectedLanguage.value === "python"
                                            ? "pip install inference-sdk"
                                            : "npm install @roboflow/inference"
                                    }\n\`\`\``}
                                    colorMode="light"
                                    sanitize={false}
                                />
                            </NumberedStep>
                        )}

                        <NumberedStep
                            number={selectedLanguage.value === "curl" ? 2 : 3}
                            title="Copy Code"
                            className="min-h-0 flex-1"
                        >
                            <div className="relative mt-2 flex min-w-0 flex-1 flex-col pb-6">
                                <div
                                    data-color-mode="light"
                                    className={twClassNames(
                                        "scrollbar-thin scrollbar-track-gray-100 scrollbar-thumb-gray-200 hover:scrollbar-thumb-gray-300 min-h-0 min-w-0 overflow-auto rounded-lg border [&>div]:m-0 [&>div]:w-full [&_pre]:m-0 [&_pre]:rounded-lg",
                                        blurred && "pointer-events-none select-none"
                                    )}
                                >
                                    <MarkdownPreview
                                        source={censorApiKeys(selectedSnippet.code)}
                                        sanitize={false}
                                    />
                                </div>
                                {blurred && (
                                    <div
                                        className={twClassNames(
                                            "absolute inset-0 flex h-full w-full items-center justify-center gap-2 p-4 opacity-100 backdrop-blur-sm transition duration-200"
                                        )}
                                    >
                                        <GetAPIKeyButton />
                                    </div>
                                )}
                            </div>
                        </NumberedStep>
                    </div>
                </div>
            </div>
        </div>
    );
};
